import { openOnboardingDialog } from "@components/utils/OnboardingDialog/utils";
import { useStatsigTracking } from "@configs/statsig/hooks";
import { AuthSource } from "@configs/statsig/types";
import { OnboardMode } from "@constants";
import useAuthSession from "@hooks/useAuthSession";
import { UserMode } from "@types";
import { MouseEvent, PropsWithChildren, ReactElement, cloneElement } from "react";

interface MemberModeProtectorProps extends PropsWithChildren {
  fallbackAction?: () => void;
  requireRegister?: boolean;
  onClick?: (e?: MouseEvent<HTMLButtonElement>) => void;
  registrationSource?: AuthSource;
}

/**
 * Allow to protect the children component based on user mode.
 * If user in OPEN mode, render a wrapper and open register drawer.
 * If user in MEMBER mode, render the children component only.
 * @param props
 * @constructor
 */
const MemberModeProtector = ({
  children,
  fallbackAction,
  requireRegister = true,
  onClick,
  registrationSource,
}: MemberModeProtectorProps) => {
  const { userMode } = useAuthSession();
  const { trackAuthClick } = useStatsigTracking();

  const handleOnClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onClick?.(e);

    // invoke fallback action if provided
    if (fallbackAction) {
      fallbackAction();
      return;
    }

    trackAuthClick(registrationSource || AuthSource.MEMBER_PROTECTOR, requireRegister);

    // open login drawer by default
    openOnboardingDialog({
      mode: requireRegister ? OnboardMode.REGISTER : OnboardMode.LOGIN,
    });
  };

  /**
   * If user in OPEN mode, render a wrapper and open login drawer.
   */
  if (userMode === UserMode.GUEST) {
    return cloneElement(children as ReactElement, {
      onClick: handleOnClick,
    });
  }

  return <>{children}</>;
};

export default MemberModeProtector;
